import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/**
 * Parses a target like "24h" or "12+" into prefix/suffix and numeric value
 */
const parse = (raw) => {
    const match = String(raw).match(/^([^\d-]*)([\d.,]+)(.*)$/);
    if (!match) return { prefix: "", num: null, suffix: raw };
    const num = parseFloat(match[2].replace(/,/g, ""));
    return {
        prefix: match[1],
        num: isNaN(num) ? null : num,
        suffix: match[3],
        decimals: (match[2].split(".")[1] || "").length,
    };
};

const formatNum = (n, decimals) => {
    if (decimals > 0) return n.toFixed(decimals);
    if (n >= 1000) return Math.round(n).toLocaleString("en-US");
    return Math.round(n).toString();
};

export const MetricStat = ({ value, label, "data-testid": testId }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });
    const [display, setDisplay] = useState("0");
    const parsed = parse(value);

    useEffect(() => {
        if (!inView || parsed.num === null) {
            setDisplay(parsed.num === null ? value : "0");
            return;
        }
        const duration = 1400;
        const start = performance.now();
        const target = parsed.num;
        let raf;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            // ease-out
            const eased = 1 - Math.pow(1 - t, 3);
            const current = target * eased;
            setDisplay(
                `${parsed.prefix}${formatNum(current, parsed.decimals)}${parsed.suffix}`,
            );
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView]);

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-start"
            data-testid={testId}
        >
            <span
                className="font-display font-semibold leading-none text-[clamp(36px,5vw,64px)] tracking-tight-2"
                style={{
                    color: "#E8FFD8",
                    textShadow: "0 0 24px rgba(124,255,79,0.12)",
                }}
            >
                {display}
            </span>
            <span className="mt-3 text-[12px] uppercase tracking-widest-2 text-text-muted">
                {label}
            </span>
        </motion.div>
    );
};

export default MetricStat;
